import React, { useState, useEffect } from 'react';
import { dashboardService } from '../services/api';
import { FiCalendar, FiDollarSign, FiUsers, FiTrendingUp } from 'react-icons/fi';
import './Dashboard.css';

const STATUS_LABELS = {
  pending: 'Pending',
  payment_completed: 'Payment completed',
  visited: 'Visited',
  cancelled: 'Cancelled',
};

const DUMMY_STATS = {
  total_bookings: 324,
  total_revenue: 524000,
  today_bookings: 127,
  this_month_revenue: 185000,
  pending_bookings: 42,
  payment_completed_bookings: 186,
  visited_bookings: 78,
  cancelled_bookings: 18,
  upcoming_visits: 124,
};

const DUMMY_RECENT_BOOKINGS = [
  { id: 1, booking_id: 'BK-2024-001', location_name: 'Universal Kingdom', full_name: 'Rajesh Kumar', mobile_number: '+91 98765 43210', total_amount: '2499.00', status: 'payment_completed' },
  { id: 5, booking_id: 'BK-2024-005', location_name: 'Universal Kingdom', full_name: 'Vikram Singh', mobile_number: '+91 76543 21098', total_amount: '2799.00', status: 'payment_completed' },
  { id: 6, booking_id: 'BK-2024-006', location_name: 'Universal Kingdom', full_name: 'Anita Desai', mobile_number: '+91 98712 34567', total_amount: '2100.00', status: 'payment_completed' },
];

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDummyData, setIsDummyData] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsResponse, bookingsResponse] = await Promise.all([
        dashboardService.getStats(),
        dashboardService.getRecentBookings(),
      ]);
      const statsData = statsResponse.data;
      const bookingsData = Array.isArray(bookingsResponse.data) ? bookingsResponse.data : [];
      const hasRealData = statsData.total_bookings > 0 || bookingsData.length > 0;
      if (hasRealData) {
        setStats(statsData);
        setRecentBookings(bookingsData);
        setIsDummyData(false);
      } else {
        setStats(DUMMY_STATS);
        setRecentBookings(DUMMY_RECENT_BOOKINGS);
        setIsDummyData(true);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setStats(DUMMY_STATS);
      setRecentBookings(DUMMY_RECENT_BOOKINGS);
      setIsDummyData(true);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => STATUS_LABELS[status] || status;

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  if (!stats) {
    return <div className="error">Failed to load dashboard data</div>;
  }

  const paymentCompleted = stats.payment_completed_bookings ?? 0;
  const visited = stats.visited_bookings ?? 0;

  return (
    <div className="dashboard">
      <h1 className="page-title">Dashboard</h1>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue">
            <FiCalendar />
          </div>
          <div className="stat-content">
            <h3>Total Bookings</h3>
            <p className="stat-value">{stats.total_bookings}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green">
            <FiDollarSign />
          </div>
          <div className="stat-content">
            <h3>Total Revenue</h3>
            <p className="stat-value">₹ {Number(stats.total_revenue).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon orange">
            <FiUsers />
          </div>
          <div className="stat-content">
            <h3>Today's Bookings</h3>
            <p className="stat-value">{stats.today_bookings}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon purple">
            <FiTrendingUp />
          </div>
          <div className="stat-content">
            <h3>This Month Revenue</h3>
            <p className="stat-value">₹ {Number(stats.this_month_revenue).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card">
          <h2>Booking Status</h2>
          <div className="status-list">
            <div className="status-item">
              <span>Pending:</span>
              <span className="status-value pending">{stats.pending_bookings}</span>
            </div>
            <div className="status-item">
              <span>Payment completed:</span>
              <span className="status-value payment-completed">{paymentCompleted}</span>
            </div>
            <div className="status-item">
              <span>Visited:</span>
              <span className="status-value visited">{visited}</span>
            </div>
            <div className="status-item">
              <span>Cancelled:</span>
              <span className="status-value cancelled">{stats.cancelled_bookings}</span>
            </div>
            <div className="status-item">
              <span>Upcoming Visits:</span>
              <span className="status-value upcoming">{stats.upcoming_visits}</span>
            </div>
          </div>
        </div>

        <div className="dashboard-card">
          <h2>Recent Bookings</h2>
          <div className="bookings-list">
            {recentBookings.length === 0 ? (
              <p className="no-data">No recent bookings</p>
            ) : (
              recentBookings.map((booking) => (
                <div key={booking.id} className="booking-item">
                  <div className="booking-info">
                    <strong>{booking.booking_id}</strong>
                    <span>{booking.location_name}</span>
                    <span className="booking-customer">{booking.full_name}</span>
                    <span className="booking-mobile">{booking.mobile_number || booking.email || '—'}</span>
                  </div>
                  <div className="booking-meta">
                    <span className={`status-badge ${booking.status}`}>{getStatusLabel(booking.status)}</span>
                    <span>₹ {parseFloat(booking.total_amount).toFixed(2)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
